import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { afterFrames } from '@/lib/animate';
import { cn } from '@/lib/utils';

export interface SearchableAction {
  id: string;
  label: string;
  icon?: LBIcon;
  iconElement?: ReactNode;
  onSelect: () => void;
  variant?: 'default' | 'destructive';
}

interface SearchableActionDropdownProps {
  actions: SearchableAction[];
  children: ReactNode;
  searchPlaceholder?: string;
  contentClassName?: string;
  maxHeight?: string;
  emptyResult?: ReactNode | string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
}

export function SearchableActionDropdown({
  actions,
  children,
  searchPlaceholder = 'Search...',
  contentClassName,
  maxHeight = 'max-h-60',
  emptyResult = 'No actions found',
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
}: SearchableActionDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filteredActions = useMemo(() => {
    if (!searchTerm.trim()) return actions;

    const searchLower = searchTerm.toLowerCase();
    return actions.filter((action) =>
      action.label.toLowerCase().includes(searchLower)
    );
  }, [searchTerm, actions]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredActions]);

  const handleSelect = (action: SearchableAction) => {
    action.onSelect();
    setIsOpen(false);
    setSearchTerm('');
    setSelectedIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        if (!filteredActions.length) return;
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) =>
          prev < filteredActions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        if (!filteredActions.length) return;
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredActions.length - 1
        );
        break;
      case 'Enter':
        if (!filteredActions.length) return;
        e.preventDefault();
        e.stopPropagation();
        if (selectedIndex >= 0 && selectedIndex < filteredActions.length) {
          handleSelect(filteredActions[selectedIndex]);
        }
        break;
      case 'Escape':
        e.stopPropagation();
        handleOpenChange(false);
        break;
      default:
        // Type-to-search: capture character and focus input
        // Skip if IME composing, or if already focused in the input
        if (
          e.key.length === 1 &&
          !e.nativeEvent.isComposing &&
          e.target !== searchInputRef.current
        ) {
          e.stopPropagation();
          // Capture the typed character so it's not lost when focusing input
          setSearchTerm((prev) => prev + e.key);
          afterFrames(2, () => {
            searchInputRef.current?.focus();
          });
        }
        // Allow Tab, Shift+Tab, and other keys to propagate for accessibility
        break;
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm('');
    } else {
      afterFrames(2, () => {
        searchInputRef.current?.focus();
      });
    }
  };

  const updateScrollFades = useCallback((e?: React.UIEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;

      setShowTopFade(scrollTop > 0);
      setShowBottomFade(scrollTop < scrollHeight - clientHeight - 1);
    }
  }, []);

  useEffect(() => {
    if (selectedIndex >= 0 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const items = container.querySelectorAll('[data-action-item]');
      const selectedItem = items[selectedIndex] as HTMLElement;

      if (selectedItem) {
        selectedItem.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (isOpen) {
      updateScrollFades();
    }
  }, [isOpen, filteredActions, updateScrollFades]);

  return (
    <DropdownMenuRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn('w-56 p-0 overflow-hidden', contentClassName)}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown}
      >
        <div className="p-2">
          <div className="relative">
            <Icons.search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-foreground-muted"
            />
            <Input
              ref={searchInputRef}
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-7 h-7 text-xs"
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        <div className="relative">
          {showTopFade && (
            <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-surface to-transparent pointer-events-none z-10" />
          )}

          <div
            ref={scrollContainerRef}
            className={cn('overflow-y-auto p-1 pt-0 relative', maxHeight)}
            onScroll={updateScrollFades}
          >
            {filteredActions.length > 0 ? (
              filteredActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <DropdownMenuItem
                    key={action.id}
                    data-action-item
                    textSize="xs"
                    variant={action.variant}
                    onPointerLeave={(event) => event.preventDefault()}
                    onPointerMove={(event) => event.preventDefault()}
                    onClick={() => handleSelect(action)}
                    className={cn(
                      'py-1.5 mb-1 scroll-my-1 transition-none hover:bg-transparent gap-2',
                      selectedIndex === index &&
                        'bg-surface-active hover:bg-surface-active'
                    )}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    {action.iconElement ??
                      (Icon && <Icon className="size-4" />)}
                    {action.label}
                  </DropdownMenuItem>
                );
              })
            ) : (
              <div className="px-2 py-3 text-xs text-foreground-muted text-center">
                {emptyResult}
              </div>
            )}
          </div>

          {showBottomFade && (
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-surface to-transparent pointer-events-none z-10" />
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
